import React, {useEffect, useMemo} from 'react';
import {classes, style} from './doppe-action-type-donate-widget.st.css';
import {DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {DoppeActionTypeDonate} from '../doppe-action-type-donate-metadata';
import {
    BymoPageViewerMode,
    useBymoPageViewerContext,
} from '../../../components/bymo-page-viewer-context/bymo-page-viewer-context';
import {DoppeActionThankYouModal} from '../../doppe-actions-common/doppe-action-thank-you-view/thankyou-view-modal';
import {useDoppeViewerClientServices} from '../../../client/doppe-user-app/doppe-viewer-client-services';
import {doppeHideableValueGetVisibleValue, useBymoPageProps} from '../../../client-server-common';
import {
    arrayDistinct,
    AsyncValueStates,
    currencyFormatPrice,
    currencyGetSymbolForDisplay,
    evaluateFunction,
    getMaxStripeChargeAmount,
    getMinStripeChargeAmount,
    HtmlObjectResizeMode
} from '@wix/devzai-utils-common';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapper
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper/doppe-action-type-widget-content-layout-new-wrapper';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {
    DoppeViewerModalPanel,
    DoppeViewerModalPanelLayout
} from '../../../components/doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {FlexLayout} from '@wix/devzai-common-client';
import {
    DoppeViewerModalPanelHeader
} from '../../../components/doppe-viewer-modal-panel-header/doppe-viewer-modal-panel-header';
import {DoppeViewerScrollView} from '../../../components/doppe-viewer-scroll-view/doppe-viewer-scroll-view';
import {
    DoppeViewerModalPanelSection
} from '../../../components/doppe-viewer-modal-panel-section/doppe-viewer-modal-panel-section';
import {
    DoppeActionCheckoutView
} from '../../doppe-actions-common/doppe-action-checkout-view/doppe-action-checkout-view';
import {DoppePageMediaView} from '../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    DoppeActionTypeWidgetInnerButton
} from '../../../components/doppe-action-type-widget-inner-button/doppe-action-type-widget-inner-button';
import {
    ButtonType,
    ListViewItemRoot,
    listViewItemRootRenderRootElementAsButton,
    SelectionList,
    TextBox,
    TextBoxAutoComplete,
    TextBoxTypes,
    useAsyncOperation,
    useFormController, useInnerWindowController
} from '@wix/devzai-utils-react';
import {DoppeActionWidgetTextBox} from '../../../components/doppe-action-widget-text-box/doppe-action-widget-text-box';
import {
    DoppeActionWidgetTextArea
} from '../../../components/doppe-action-widget-text-area/doppe-action-widget-text-area';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {doppeAppLayoutGap} from '@wix/doppe-ui';
import {useDoppeViewerLocales} from '../../../client/doppe-viewer-locales/doppe-viewer-locales';

export const DoppeActionTypeDonateWidget = React.memo(function DoppeActionTypeDonateWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeDonate.ActionSettings>,
) {
    const {
        actionContext,
        ...htmlAttributes
    } = props;

    const {
        amountOptions,
        showCustomAmount,
        showMessageInput,
        previewCheckoutButtonText,
        amountSelectionTitle,
        messageInputTitle,
        showAddressInput,
        showPhoneInput,
        showEmailInput,
        showConsent,
        showConsentCheckbox,
        customConsentText,
        currencyCode,
        images,
        description,
        customThankYouMessage,
    } = actionContext.action.settings;

    const clientServices = useDoppeViewerClientServices();
    const pageViewerContext = useBymoPageViewerContext();
    const doppeViewerLocales = useDoppeViewerLocales();
    const lang = useBymoPageProps().lang;
    const customInputRef = React.useRef<TextBox>(null);

    const customValue = useMemo(() => {
        return {
            value: 'custom',
        } as const;
    }, []);

    const amounts = useMemo(() => {
        const amounts: { value: number }[] = arrayDistinct(amountOptions)
            .map((amount) => ({value: amount}))
            .filter((amount) => amount.value > 0);
        return amounts;
    }, [amountOptions]);

    const initialAmount = amounts[0]?.value ?? 0;

    const minAmount = getMinStripeChargeAmount(currencyCode) ?? 1;
    const maxAmount = getMaxStripeChargeAmount(currencyCode);

    const formController = useFormController<{
        amount: number;
        message: string;
    }>(
        {
            message: '',
            amount: initialAmount,
        },
        {
            validationFunction: (formData, errorsReporter) => {
                if (!formData.amount) {
                    errorsReporter.report('amount', doppeViewerLocales["donate.errors.validation.selectAmount"]);
                } else if (formData.amount < minAmount || formData.amount > maxAmount) {
                    const min = currencyFormatPrice({price: minAmount, code: currencyCode, locale: lang});
                    const max = currencyFormatPrice({price: maxAmount, code: currencyCode, locale: lang});
                    errorsReporter.report('amount', doppeViewerLocales["donate.errors.validation.selectAmountBetween"]({min: min, max: max}));
                } else if (formData.amount.toString().split('.')[1]?.length > 2) {
                    errorsReporter.report('amount', doppeViewerLocales["donate.errors.validation.selectAmountWithDecimal"]);
                }
            },
        },
    );

    const [showGeneralError, setShowGeneralError] = React.useState(false);

    const amountError = formController.formFieldsErrors.amount;
    const [formSubmissionOperation, setFormSubmissionOperation] = useAsyncOperation();

    const [previousAmount, setPreviousAmount] = React.useState<number>(initialAmount);

    const windowController = useInnerWindowController();
    const onSubmit = formController.createSubmitEventHandler(async (formData) => {
        try {
            await setFormSubmissionOperation(
                evaluateFunction(async () => {
                    const price = {
                        amount: formData.amount,
                        currencyCode: currencyCode,
                    };

                    const isCustomAmount = selectedItem.value === 'custom';

                    if (pageViewerContext.mode === BymoPageViewerMode.Live) {
                        clientServices.viewerAnalyticsClient.logEvent('uouCreatePaymentIntent', {
                            ...actionContext.getBiIdFields(),
                            value: `${formData.amount}`,
                            noteAdded: formData.message?.trim().length > 0,
                            isCustomAmount: isCustomAmount,
                        });
                    }

                    const createStripePaymentIntent = () => actionContext.createStripePaymentIntent({
                        price: price,
                    });

                    actionContext.modalsController.showInnerModal({
                        content: ({modalController}) => (
                            <DoppeViewerModalPanel
                                className={style(classes.modal, FlexLayout.column.default)}
                                mobileLayout={DoppeViewerModalPanelLayout.FullScreen}
                                desktopLayout={DoppeViewerModalPanelLayout.InnerCentered}
                            >
                                <DoppeViewerModalPanelHeader
                                    title={previewCheckoutButtonText}
                                    dismissButtonCallback={() => {
                                        modalController.closeWindow();
                                    }}
                                />
                                <DoppeViewerScrollView
                                    className={style(classes.scrollView, FlexLayout.fillRemainingVerticalSpace)}
                                >
                                    <DoppeViewerModalPanelSection
                                        className={style(classes.modalContent, FlexLayout.column.default)}
                                    >
                                        <div
                                            className={style(classes.modalBody, FlexLayout.column.default)}
                                        >
                                            <DoppeActionCheckoutView
                                                onDone={({
                                                             name,
                                                             paymentIntentId,
                                                             consent
                                                         }) => {
                                                    clientServices
                                                        .getApiClient()
                                                        .notifyDonateSuccess({
                                                            paymentIntentId,
                                                            uouMessage: formData.message,
                                                            emailSubscriptionConsent: consent,
                                                        })
                                                        .catch(console.error);

                                                    modalController.closeWindowImmediately();
                                                    actionContext.reportGoalAchieved();
                                                    windowController?.closeWindow();

                                                    actionContext.modalsController.showInnerModal({
                                                        content: ({modalController}) => (
                                                            <DoppeActionThankYouModal
                                                                dismissButtonCallback={() => {
                                                                    modalController.closeWindow();
                                                                }}
                                                                onConfirm={() => {
                                                                    modalController.closeWindow();
                                                                }}
                                                                name={name}
                                                                price={price}
                                                                customThankYouMessage={customThankYouMessage}
                                                            />
                                                        ),
                                                    });
                                                }}
                                                actionContext={actionContext}
                                                showPhoneInput={showPhoneInput}
                                                showEmailInput={showEmailInput}
                                                showAddressInput={showAddressInput}
                                                price={price}
                                                createStripePaymentIntent={createStripePaymentIntent}
                                                demoMode={pageViewerContext.mode !== BymoPageViewerMode.Live}
                                                isCustomAmount={isCustomAmount}
                                                showConsent={showConsent}
                                                showConsentCheckbox={showConsentCheckbox}
                                                consentText={doppeHideableValueGetVisibleValue(customConsentText, undefined)}
                                            />
                                        </div>
                                    </DoppeViewerModalPanelSection>
                                </DoppeViewerScrollView>
                            </DoppeViewerModalPanel>
                        )
                    })
                }),
            );
        } catch (error) {
            setShowGeneralError(true);
            console.error(error);
        }
    });

    const [selectedItem, setSelectedItem] = React.useState<{ value: 'custom' | number }>(amounts?.[0] || customValue);
    const [shouldFocusCustomAmountInput, setShouldFocusCustomAmountInput] = React.useState(false);

    const customAmountEnabled = showCustomAmount || amounts.length === 0;
    const customAmountInputIsVisible = selectedItem.value === 'custom' || amounts.length === 0;

    const listItems = customAmountEnabled && amounts.length > 0 ? [...amounts, customValue] : amounts;

    useEffect(() => {
        if (customAmountInputIsVisible) {
            setSelectedItem(customValue);
            // formController.setValue('amount', 0);
        }
        if (shouldFocusCustomAmountInput) {
            setShouldFocusCustomAmountInput(false);
            customInputRef.current?.focus();
        }
    }, [customAmountInputIsVisible, shouldFocusCustomAmountInput, customValue, formController]);

    const onChange = (options: {
        type: string,
        value: string,
    }) => {
        const {
            type,
            value,
        } = options;

        if (pageViewerContext.mode === BymoPageViewerMode.Live) {
            clientServices.viewerAnalyticsClient.logEvent('uouInputEdited', {
                ...actionContext.getBiIdFields(),
                inputName: type,
                value: value,
            });
        }
    }

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            actionContext={actionContext}
            isForm={true}
            onSubmit={onSubmit}
            noValidate={true}
        >
            <DoppeActionTypeWidgetContentLayoutNewWrapper
                fullWidthMainMedia={true}
                mainMedia={images && images.length > 0 ? (
                    <DoppePageMediaView
                        className={style(classes.productMedia, {})}
                        mediaResource={images[0]}
                        layoutSpec={{autoHeight: true, maxHeight: 360}}
                        widthSpec={{contentRelativeWidth: 1}}
                        mediaResizeMode={HtmlObjectResizeMode.Cover}
                    />
                ) : null}
                footer={(
                    <>
                        <DoppeActionTypeWidgetInnerButton
                            buttonType={ButtonType.SubmitButton}
                            isLoading={formSubmissionOperation?.state === AsyncValueStates.Pending}
                        >
                            {previewCheckoutButtonText}
                        </DoppeActionTypeWidgetInnerButton>
                        {showGeneralError ? (
                            <div
                                className={style(classes.generalErrorMessage, {})}
                            >{doppeViewerLocales["donate.errors.general"]}</div>
                        ) : null}
                    </>
                )}
            >
                <DoppeActionTypeWidgetInnerTitle>
                    {amountSelectionTitle}
                </DoppeActionTypeWidgetInnerTitle>
                <DoppeActionTypeWidgetInnerDescription
                    className={style(classes.description, {})}
                >
                    {description}
                </DoppeActionTypeWidgetInnerDescription>
                <div
                    className={style(classes.formFields, {}, FlexLayout.column.default, doppeAppLayoutGap(18))}
                >
                    {listItems.length > 1 ? (
                            <SelectionList
                                className={style(classes.selectionList)}
                                value={selectedItem}
                                items={listItems}
                                onKeyPress={onSubmit}
                                onChange={(item) => {
                                    setSelectedItem(item);
                                    const value = item.value;
                                    if (value !== 'custom') {
                                        formController.setValue('amount', value);
                                    } else {
                                        setShouldFocusCustomAmountInput(true);
                                    }
                                    onChange({
                                        type: 'amountSelectionList',
                                        value: value.toString(),
                                    });
                                }}
                                renderItem={(renderProps) => {
                                    const value = typeof renderProps.dataItem.value === 'number'
                                        ? currencyFormatPrice({
                                            code: currencyCode,
                                            price: renderProps.dataItem.value,
                                            locale: lang,
                                        })
                                        : doppeViewerLocales["donate.paymentOptions.custom"];
                                    return (
                                        <ListViewItemView
                                            isSelected={renderProps.isSelected}
                                            value={value}
                                        />
                                    );
                                }}
                            />
                        ) :
                        listItems.length === 1 ? <div className={style(classes.oneItem, {})}>${amounts[0].value}</div> :
                            null
                    }
                    {customAmountEnabled ? (
                        <div
                            className={style(classes.customAmountContainer, {
                                hide: !customAmountInputIsVisible,
                            })}
                        >
                            <DoppeActionWidgetTextBox
                                disabled={!customAmountInputIsVisible}
                                ref={customInputRef}
                                className={style(classes.customAmountTextBox, {})}
                                prefixContent={
                                    <span
                                        style={{whiteSpace: 'pre'}}>{currencyGetSymbolForDisplay(currencyCode) + ' '}</span>
                                }
                                hasError={amountError !== undefined}
                                type={TextBoxTypes.Number}
                                autoSelectTextOnFocus={false}
                                autoComplete={TextBoxAutoComplete.PersonFullName}
                                shouldValidateValue={false}
                                value={formController.getValue('amount')}
                                onTextChange={(value) => {
                                    if (value.length <= maxAmount.toString().length) {
                                        formController.setValue('amount', Number(value));
                                    }
                                    if (value.length > 1 && value.startsWith('0') && value[1] !== '.') {
                                        // prevents leading zeros
                                        const element = customInputRef.current?.getInputElement();
                                        if (element) {
                                            element.value = value.substring(1);
                                        }
                                    }
                                }}
                                onFocus={() => {
                                    setPreviousAmount(formController.getValue('amount'));
                                }}
                                onBlur={() => {
                                    if (formController.getValue('amount') !== previousAmount) {
                                        onChange({
                                            type: 'customAmount',
                                            value: formController.getValue('amount').toString(),
                                        });
                                    }
                                }}
                            />
                            {amountError !== undefined ? (
                                <div className={style(classes.inputErrorMessage, {})}>{amountError}</div>
                            ) : null}
                        </div>
                    ) : null}
                    {showMessageInput ? (
                        <div
                            className={style(classes.formField, {}, FlexLayout.column.default, doppeAppLayoutGap(8))}
                        >
                            <DoppeActionTypeWidgetInnerTitle>
                                {messageInputTitle}
                            </DoppeActionTypeWidgetInnerTitle>
                            <DoppeActionWidgetTextArea
                                className={style(classes.messageTextBox, {})}
                                value={formController.getValue('message')}
                                onTextChange={(value) => {
                                    formController.setValue('message', value);
                                }}
                                onBlur={() => {
                                    onChange({
                                        type: 'message',
                                        value: formController.getValue('message').toString(),
                                    });
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            </DoppeActionTypeWidgetContentLayoutNewWrapper>
        </DoppeActionTypeWidgetLayout>
    );
});

const ListViewItemView = React.memo(function ListViewItemView(props: { value: string | number; isSelected: boolean }) {
    const {
        value,
        isSelected,
    } = props;

    return (
        <ListViewItemRoot
            className={style(classes.listViewItemViewRoot, {
                isSelected: isSelected,
            })}
            renderRootElement={listViewItemRootRenderRootElementAsButton}
        >
            {value}
        </ListViewItemRoot>
    );
});
